'use client';

import { useEffect, useState } from 'react';
import { Check, MapPinHouse } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { useDeviceType } from '@/hooks/useIsMobile';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import { Prefecture } from '@/types/users';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import ErrorBox from '@/components/ErrorBox';
import LabelBadge from '@/components/LabelBadge';
import SectionTitle from '@/components/SectionTitle';
import FormFieldLabel from '@/components/ui/form/FormFieldLabel';
import FormInputWithLabel from '@/components/ui/form/FormInputWithLabel';
import FormSectionTitle from '@/components/ui/form/FormSectionTitle';
import FormSelect from '@/components/ui/form/FormSelect';

export default function DashboardPersonalShipping() {
    const { handleError, errorMessage, clearError } = useHandleError();
    const closeModal = useModalStore((state) => state.closeModal);
    const showToastAsync = useToastStore((s) => s.showToastAsync);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isZipProcessing, setIsZipProcessing] = useState(false);
    const [prefecturesList, setPrefecturesList] = useState([]);
    const { isMobile } = useDeviceType();

    const setUserRegistrationStatus = useAuthStore((s) => s.setUserRegistrationStatus);
    const [formData, setFormData] = useState({
        recipient_name: '',
        recipient_name_kana: '',
        recipient_phone_number: '',
        zip: '',
        prefecture_cd: '',
        address_1: '',
        address_2: '',
    });

    const handleFormDataChange = (target: string, value: string) => {
        setFormData({
            ...formData,
            [target]: value ?? '',
        });
    };

    useEffect(() => {
        (async () => {
            try {
                const prefectureData = await apiFetch(`/prefectures`);
                const options = prefectureData.map(({ cd, name }: Prefecture) => ({
                    value: cd,
                    label: name,
                }));
                setPrefecturesList(options);
            } catch (err: unknown) {
                handleError(err);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const userAddress = await apiFetch(`/users/me/address/main`);
                if (userAddress.address_id) {
                    setFormData(userAddress);
                }
            } catch (err: unknown) {
                handleError(err);
            }
        })();
    }, []);

    async function handleSubmit() {
        try {
            clearError();
            setIsProcessing(true);
            await apiFetch(`/users/me/address/main`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            closeModal();
            setUserRegistrationStatus({ address: 1 });
            await showToastAsync('お届け先の設定が完了しました', 'success');
        } catch (err: unknown) {
            handleError(err);
            setIsProcessing(false);
        }
    }

    async function zipToAddressSubmit() {
        clearError();
        setIsZipProcessing(true);
        try {
            const data = await apiFetch(`/users/me/prefecture`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ zip: formData.zip }),
            });
            setFormData({
                ...formData,
                prefecture_cd: data[3],
                address_1: data[1] + data[2],
            });
            setIsZipProcessing(false);
        } catch (err: unknown) {
            handleError(err);
            setIsZipProcessing(false);
        }
    }

    useEffect(() => {
        if (!errorMessage?.errors?.length) return;
        const firstError = errorMessage.errors[0];
        const fieldName = Array.isArray(firstError.loc) ? firstError.loc[1] : String(firstError.loc);
        const element = document.getElementById(`${fieldName}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [errorMessage]);

    return (
        <div className="flex flex-col w-full md:w-[98%] section-4-b bg-color-white-base">
            <div className="flex w-full items-center">
                <div className="flex flex-grow justify-start items-center w-full">
                    <SectionTitle title={`Shipping`} />
                </div>
            </div>
            <div className="flex flex-col w-full">
                <div className="section-spacer-inner-y"></div>
                <div className="flex justify-start items-center w-full py-4">
                    <FormSectionTitle title="お届け先" />
                </div>
                <div className="flex flex-col items-start justify-start w-full py-4">
                    <div id="recipient_name" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full mb-2">
                            <FormFieldLabel title="お届け先氏名" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="recipient_name" />
                        </div>
                        <div className="flex w-full">
                            <FormInputWithLabel
                                id="recipient_name"
                                name="recipient_name"
                                label="20文字以内"
                                type="text"
                                maxLength={20}
                                autoComplete="off"
                                value={formData.recipient_name}
                                onChange={(e) => handleFormDataChange('recipient_name', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="py-6 md:py-4"></div>
                    <div id="recipient_name_kana" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full mb-2">
                            <FormFieldLabel title="お届け先氏名カナ" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="recipient_name_kana" />
                        </div>
                        <div className="flex w-full">
                            <FormInputWithLabel
                                id="recipient_name_kana"
                                name="recipient_name_kana"
                                label="20文字以内"
                                type="text"
                                maxLength={20}
                                autoComplete="off"
                                value={formData.recipient_name_kana}
                                onChange={(e) => handleFormDataChange('recipient_name_kana', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="py-6 md:py-4"></div>
                    <div id="recipient_phone_number" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full mb-2">
                            <FormFieldLabel title="お届け先電話番号" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="recipient_phone_number" />
                        </div>
                        <div className="flex w-full">
                            <FormInputWithLabel
                                id="recipient_phone_number"
                                name="recipient_phone_number"
                                label="11桁以内の半角数字"
                                type="text"
                                maxLength={11}
                                autoComplete="off"
                                value={formData.recipient_phone_number}
                                inputMode="numeric"
                                onChange={(e) => handleFormDataChange('recipient_phone_number', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="py-6 md:py-4"></div>
                    <div id="zip" className="flex flex-col items-center w-full">
                        <div className="flex items-start justify-start w-full">
                            <ErrorBox message={errorMessage?.message} />
                        </div>
                        <div className="flex justify-start items-center w-full mb-2">
                            <FormFieldLabel title="郵便番号" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2 lg:pl-8">
                            <ErrorBox errors={errorMessage?.errors} field="zip" />
                        </div>
                        <div className="flex flex-row w-full">
                            <div className="flex w-full pb-6 lg:pb-0">
                                <div className="font-corp justify-center items-center text-sm md:text-base pt-2 px-2 md:px-4 translate-y-[6px]">
                                    〒
                                </div>
                                <FormInputWithLabel
                                    id="zip"
                                    name="zip"
                                    label="7桁の半角数字"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={7}
                                    autoComplete="postal-code"
                                    value={formData?.zip ?? ''}
                                    onChange={(e) => handleFormDataChange('zip', e.target.value)}
                                />
                            </div>
                            <div className="flex justify-center items-start pt-1 lg:pt-0 ml-4 lg:ml-12">
                                <ButtonGAdaptive
                                    type="button"
                                    label="住所検索"
                                    icon={MapPinHouse}
                                    size={isMobile ? 'xs' : 'md'}
                                    colorType={1}
                                    onClick={zipToAddressSubmit}
                                    isProcessing={isZipProcessing}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="py-4 md:py-4"></div>
                    <div id="prefecture_cd" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full mb-2">
                            <FormFieldLabel title="都道府県" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="prefecture_cd" />
                        </div>
                        <div className="flex w-full">
                            <FormSelect
                                id="prefecture"
                                name="prefecture"
                                value={formData.prefecture_cd}
                                onChange={(e) => handleFormDataChange('prefecture_cd', e.target.value)}
                                options={[{ value: '', label: '都道府県を選択してください' }, ...prefecturesList]}
                                className="w-full"
                            />
                        </div>
                        <div className="py-6 md:py-4"></div>
                        <div id="address_1" className="flex flex-col items-center w-full">
                            <div className="flex justify-start items-center w-full mb-2">
                                <FormFieldLabel title="市区町村・町名" />
                                <LabelBadge type="required" />
                            </div>
                            <div className="flex w-full justify-start my-2">
                                <ErrorBox errors={errorMessage?.errors} field="address_1" />
                            </div>
                            <div className="flex w-full">
                                <FormInputWithLabel
                                    id="address_1"
                                    name="address_1"
                                    label="30文字以内"
                                    type="text"
                                    maxLength={30}
                                    autoComplete="off"
                                    value={formData.address_1}
                                    onChange={(e) => handleFormDataChange('address_1', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="py-6 md:py-4"></div>
                        <div id="address_2" className="flex flex-col items-center w-full">
                            <div className="flex justify-start items-center w-full mb-2">
                                <FormFieldLabel title="番地・その他" />
                                <LabelBadge type="required" />
                            </div>
                            <div className="flex w-full justify-start my-2">
                                <ErrorBox errors={errorMessage?.errors} field="address_2" />
                            </div>
                            <div className="flex w-full">
                                <FormInputWithLabel
                                    id="address_2"
                                    name="address_2"
                                    label="30文字以内"
                                    type="text"
                                    maxLength={30}
                                    autoComplete="off"
                                    value={formData.address_2}
                                    onChange={(e) => handleFormDataChange('address_2', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="py-6 md:py-4"></div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col items-start justify-start w-full py-2">
                <div className="flex flex-col-reverse md:flex-row justify-center w-full pt-12">
                    <div className="flex-grow"></div>
                    <div className="flex flex-col justify-end items-center">
                        <div className="flex justify-center items-center">
                            <ButtonGAdaptive
                                type="submit"
                                label="設定する"
                                onClick={handleSubmit}
                                description="set up shipping"
                                icon={Check}
                                isProcessing={isProcessing}
                            />
                        </div>
                    </div>
                    <div className="flex-grow"></div>
                </div>
            </div>
        </div>
    );
}
